import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { parseJwt } from '@/core/utils/jwtUtils';
import { restoreState } from '@/modules/auth/redux/authSlice';

/**
 * Auth state initialization hook
 * This hook initializes auth state from localStorage on application startup
 * and ensures the Redux store is properly synced with the stored auth state
 */
export const useAuthInit = () => {
  const [initialized, setInitialized] = useState(false);
  const isAuthenticated = useSelector((state: any) => state.auth.isAuthenticated);
  const dispatch = useDispatch();

  useEffect(() => {
    const initializeAuth = () => {
      try {
        // If already authenticated in Redux, no need to initialize
        if (isAuthenticated) {
          setInitialized(true);
          return;
        }

        // Check localStorage for auth state
        const storedAuthStr = localStorage.getItem('authState');
        if (!storedAuthStr) {
          setInitialized(true);
          return;
        }

        const storedAuth = JSON.parse(storedAuthStr);
        if (!storedAuth?.accessToken || !storedAuth?.refreshToken) {
          setInitialized(true);
          return;
        }

        // Check if token is valid
        const decodedToken = parseJwt(storedAuth.accessToken);

        // If we have a valid token, restore auth state in Redux
        if (decodedToken) {
          dispatch(
            restoreState({
              ...storedAuth,
              isAuthenticated: true,
            })
          );
        }
      } catch (error) {
        void error;
      } finally {
        setInitialized(true);
      }
    };

    initializeAuth();
  }, [dispatch, isAuthenticated]);

  return { initialized };
};
